import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiFetch } from '../lib/api';
import { industryLabel } from '../lib/tenant';
import { Icons } from '../components/icons';

const INDUSTRIES = [
  { key: '', label: '全部行业' },
  { key: 'assembly', label: '装配' },
  { key: 'welding', label: '焊接' },
  { key: 'qc', label: '质检' },
  { key: 'electronics', label: '电子' },
];

const STEP_TYPES = [
  { key: 'check', label: '核对' },
  { key: 'action', label: '操作' },
  { key: 'qc', label: '质检' },
  { key: 'param', label: '参数' },
  { key: 'measure', label: '测量' },
  { key: 'record', label: '记录' },
  { key: 'auto', label: '自动' },
  { key: 'test', label: '测试' },
];

function stepTypeLabel(t) {
  return STEP_TYPES.find(x => x.key === t)?.label || t || '步骤';
}

function getSteps(content) {
  const steps = content?.steps;
  return Array.isArray(steps) ? steps : [];
}

function emptyForm() {
  return {
    id: '',
    industry: 'assembly',
    name: '',
    version: '1.0.0',
    description: '',
    steps: [{ id: 's1', title: '', type: 'action' }],
    showJson: false,
    contentJson: '{"steps":[]}',
  };
}

function formFromTemplate(t) {
  const content = t.content || {};
  const steps = getSteps(content).map((s, i) => ({
    id: s.id || ('s' + (i + 1)),
    title: s.title || '',
    type: s.type || 'action',
  }));
  return {
    id: t.id,
    industry: t.industry || 'assembly',
    name: t.name || '',
    version: t.version || '1.0.0',
    description: content.description || '',
    steps: steps.length ? steps : [{ id: 's1', title: '', type: 'action' }],
    showJson: false,
    contentJson: JSON.stringify(content, null, 2),
  };
}

function buildContent(form) {
  const steps = (form.steps || [])
    .filter(s => (s.title || '').trim())
    .map((s, i) => ({
      id: s.id || ('s' + (i + 1)),
      title: s.title.trim(),
      type: s.type || 'action',
    }));
  const content = { steps };
  if ((form.description || '').trim()) content.description = form.description.trim();
  return content;
}

export function PlatformTemplates() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [industry, setIndustry] = useState('');
  const [q, setQ] = useState('');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    apiFetch('/api/v1/platform/templates').then(d => {
      setList(d.data || []);
      setLoading(false);
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = { '': list.length };
    INDUSTRIES.forEach(i => { if (i.key) c[i.key] = 0; });
    list.forEach(t => {
      if (c[t.industry] != null) c[t.industry]++;
      else c[t.industry] = 1;
    });
    return c;
  }, [list]);

  const filtered = useMemo(() => {
    const kw = q.trim().toLowerCase();
    return list.filter(t => {
      if (industry && t.industry !== industry) return false;
      if (!kw) return true;
      return (t.name || '').toLowerCase().includes(kw)
        || (t.version || '').toLowerCase().includes(kw)
        || industryLabel(t.industry).includes(kw);
    });
  }, [list, industry, q]);

  const openCreate = () => { setForm(emptyForm()); setModal('create'); };
  const openEdit = (t) => { setForm(formFromTemplate(t)); setModal('edit'); };

  const save = async () => {
    if (!form.name.trim()) return alert('请填写模板名称');
    let content;
    if (form.showJson) {
      try { content = JSON.parse(form.contentJson); }
      catch { return alert('content 必须是合法 JSON'); }
    } else {
      content = buildContent(form);
      if (!getSteps(content).length) return alert('请至少添加一个步骤');
    }
    setBusy(true);
    const body = {
      industry: form.industry,
      name: form.name.trim(),
      version: form.version || '1.0.0',
      content,
    };
    const d = modal === 'create'
      ? await apiFetch('/api/v1/platform/templates', { method: 'POST', body })
      : await apiFetch('/api/v1/platform/templates/' + form.id, { method: 'PUT', body });
    setBusy(false);
    if (d.code === 0) { setModal(null); load(); }
    else alert(d.message || '保存失败');
  };

  const remove = async (t) => {
    if (!confirm('确认删除模板「' + t.name + '」？')) return;
    const d = await apiFetch('/api/v1/platform/templates/' + t.id, { method: 'DELETE' });
    if (d.code === 0) load(); else alert(d.message);
  };

  const seed = async () => {
    setBusy(true);
    const d = await apiFetch('/api/v1/platform/templates/seed', { method: 'POST' });
    setBusy(false);
    if (d.code === 0) { alert('已导入 ' + (d.data?.inserted || 0) + ' 个示例模板'); load(); }
    else alert(d.message);
  };

  const updateStep = (idx, patch) => {
    setForm(prev => ({
      ...prev,
      steps: prev.steps.map((s, i) => i === idx ? { ...s, ...patch } : s),
    }));
  };

  const addStep = () => {
    setForm(prev => ({
      ...prev,
      steps: [...prev.steps, { id: 's' + (prev.steps.length + 1), title: '', type: 'action' }],
    }));
  };

  const removeStep = (idx) => {
    setForm(prev => ({
      ...prev,
      steps: prev.steps.length <= 1 ? prev.steps : prev.steps.filter((_, i) => i !== idx),
    }));
  };

  return (
    <div className="pg">
      <div className="pg-hero">
        <div>
          <div className="pg-kicker">内容与终端</div>
          <h1 className="pg-title">行业模板</h1>
          <p className="pg-sub">预置 SOP / 作业指导书骨架，供租户快速创建行业标准流程</p>
        </div>
        <div className="pg-hero-actions">
          <button className="btn btn-outline btn-xs" onClick={seed} disabled={busy}>导入示例</button>
          <button className="btn btn-primary" onClick={openCreate}><Icons.Plus />新建模板</button>
        </div>
      </div>

      <div className="pg-chips">
        {INDUSTRIES.map(f => (
          <button
            key={f.key || 'all'}
            type="button"
            className={'pg-chip' + (industry === f.key ? ' active' : '')}
            onClick={() => setIndustry(f.key)}
          >
            <span>{f.label}</span>
            <strong>{counts[f.key] ?? 0}</strong>
          </button>
        ))}
      </div>

      <div className="pg-toolbar">
        <input
          className="form-input pg-search"
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="搜索模板名称、版本…"
        />
        <span className="pg-toolbar-meta">共 {filtered.length} / {list.length} 个模板</span>
        <button className="btn btn-outline btn-xs" onClick={load} disabled={loading}>
          <Icons.Refresh w={12} h={12} /> 刷新
        </button>
      </div>

      {loading ? (
        <div className="card"><div className="empty"><span className="spinner" /></div></div>
      ) : filtered.length === 0 ? (
        <div className="card">
          <div className="empty">
            <Icons.Empty />
            <div className="empty-text">{list.length === 0 ? '暂无模板' : '无匹配结果'}</div>
            {list.length === 0 && (
              <button className="btn btn-outline btn-xs" style={{ marginTop: 12 }} onClick={seed} disabled={busy}>
                导入示例模板
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="tpl-grid">
          {filtered.map(t => {
            const steps = getSteps(t.content);
            const isM1E = !!(t.content?.m1e_standard);
            return (
              <div key={t.id} className="tpl-card">
                <div className="tpl-card-head">
                  <div className="tpl-card-tags">
                    <span className="tag tag-blue">{industryLabel(t.industry)}</span>
                    {isM1E && <span className="tag tag-orange">4M1E</span>}
                    <span className="tpl-ver">v{t.version || '—'}</span>
                  </div>
                  <div className="tpl-card-actions">
                    <button className="btn btn-outline btn-xs" onClick={() => setPreview(t)}>预览</button>
                    <button className="btn btn-outline btn-xs" onClick={() => openEdit(t)}>编辑</button>
                    <button className="btn btn-outline btn-xs danger-text" onClick={() => remove(t)}>删除</button>
                  </div>
                </div>
                <div className="tpl-card-name">{t.name}</div>
                {t.content?.description && (
                  <p className="tpl-card-desc">{t.content.description}</p>
                )}
                <div className="tpl-card-meta">
                  <span>{steps.length} 个步骤</span>
                  <span>{new Date(t.created_at).toLocaleDateString('zh-CN')}</span>
                </div>
                <ol className="tpl-step-preview">
                  {steps.slice(0, 4).map((s, i) => (
                    <li key={s.id || i}>
                      <span className="tpl-step-type">{stepTypeLabel(s.type)}</span>
                      <span className="tpl-step-title">{s.title || '未命名步骤'}</span>
                    </li>
                  ))}
                  {steps.length > 4 && <li className="tpl-step-more">另有 {steps.length - 4} 步…</li>}
                  {steps.length === 0 && <li className="tpl-step-more">暂无步骤</li>}
                </ol>
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="modal" style={{ width: 600 }}>
            <div className="modal-header">{modal === 'create' ? '新建行业模板' : '编辑模板'}</div>
            <div className="form-row-2">
              <div className="form-group">
                <label>行业</label>
                <select className="form-input" value={form.industry} onChange={e => setForm({ ...form, industry: e.target.value })}>
                  {INDUSTRIES.filter(i => i.key).map(i => (
                    <option key={i.key} value={i.key}>{i.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>版本</label>
                <input className="form-input" value={form.version} onChange={e => setForm({ ...form, version: e.target.value })} placeholder="1.0.0" />
              </div>
            </div>
            <div className="form-group">
              <label>模板名称</label>
              <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="如: 标准装配作业指导书" />
            </div>

            {!form.showJson ? (
              <>
                <div className="form-group">
                  <label>说明（可选）</label>
                  <input className="form-input" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="简要说明模板用途" />
                </div>
                <div className="form-group">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <label style={{ margin: 0 }}>步骤</label>
                    <button type="button" className="btn btn-outline btn-xs" onClick={addStep}><Icons.Plus /> 添加步骤</button>
                  </div>
                  <div className="tpl-step-editor">
                    {form.steps.map((s, idx) => (
                      <div key={idx} className="tpl-step-row">
                        <span className="tpl-step-idx">{idx + 1}</span>
                        <select className="form-input" value={s.type} onChange={e => updateStep(idx, { type: e.target.value })}>
                          {STEP_TYPES.map(st => <option key={st.key} value={st.key}>{st.label}</option>)}
                        </select>
                        <input className="form-input" value={s.title} onChange={e => updateStep(idx, { title: e.target.value })} placeholder="步骤标题" />
                        <button type="button" className="btn btn-outline btn-xs danger-text" onClick={() => removeStep(idx)} disabled={form.steps.length <= 1}>删</button>
                      </div>
                    ))}
                  </div>
                  <p className="form-tip">复杂结构（如 4M1E）请用「高级 JSON」编辑，以免丢失扩展字段。</p>
                </div>
              </>
            ) : (
              <div className="form-group">
                <label>内容 (JSON)</label>
                <textarea
                  className="form-input"
                  rows={12}
                  style={{ fontFamily: 'monospace', fontSize: 12 }}
                  value={form.contentJson}
                  onChange={e => setForm({ ...form, contentJson: e.target.value })}
                />
              </div>
            )}

            <button
              type="button"
              className="wb-link"
              style={{ marginBottom: 12 }}
              onClick={() => {
                if (!form.showJson) {
                  setForm({
                    ...form,
                    showJson: true,
                    contentJson: JSON.stringify(buildContent(form), null, 2),
                  });
                } else {
                  try {
                    const content = JSON.parse(form.contentJson || '{}');
                    const next = formFromTemplate({
                      id: form.id,
                      industry: form.industry,
                      name: form.name,
                      version: form.version,
                      content,
                    });
                    setForm({ ...next, showJson: false, contentJson: form.contentJson });
                  } catch {
                    alert('JSON 无效，无法切回表单模式');
                  }
                }
              }}
            >
              {form.showJson ? '切回表单编辑' : '高级：JSON 编辑'}
            </button>

            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setModal(null)}>取消</button>
              <button className="btn btn-primary" disabled={busy} onClick={save}>{busy ? '保存中…' : '保存'}</button>
            </div>
          </div>
        </div>
      )}

      {preview && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setPreview(null)}>
          <div className="modal" style={{ width: 560 }}>
            <div className="modal-header">{preview.name}</div>
            <div className="tpl-preview-meta">
              <span className="tag tag-blue">{industryLabel(preview.industry)}</span>
              {preview.content?.m1e_standard && <span className="tag tag-orange">4M1E</span>}
              <span className="tpl-ver">v{preview.version}</span>
              <span className="cell-muted">{new Date(preview.created_at).toLocaleString('zh-CN')}</span>
            </div>
            {preview.content?.description && <p className="tpl-card-desc" style={{ marginBottom: 12 }}>{preview.content.description}</p>}
            <ol className="tpl-preview-list">
              {getSteps(preview.content).map((s, i) => (
                <li key={s.id || i}>
                  <div className="tpl-preview-item-head">
                    <strong>{i + 1}. {s.title || '未命名'}</strong>
                    <span className="tag tag-gray">{stepTypeLabel(s.type)}</span>
                  </div>
                  {s.content && <p className="tpl-preview-body">{s.content}</p>}
                </li>
              ))}
              {getSteps(preview.content).length === 0 && <li className="cell-muted">暂无步骤</li>}
            </ol>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setPreview(null)}>关闭</button>
              <button className="btn btn-primary" onClick={() => { setPreview(null); openEdit(preview); }}>编辑</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
